import { useFormContext } from "react-hook-form";

import { VALUE_INPUT, type ValueInputKind } from "../conditions";
import {
  operatorSchema,
  type FormCondition,
  type SegmentFormValues,
} from "../schemas";

import { OperatorValue } from "@/config/operatorConfig";
import type { SegmentField } from "@/features/segments/types";

type UseConditionEditorParams = {
  ruleIndex: number;
  conditionIndex: number;
  fields: SegmentField[];
};

const getValueInput = (operator: OperatorValue): ValueInputKind => {
  if (operator === OperatorValue.IS_NULL) return VALUE_INPUT.NONE;
  if (operator === OperatorValue.IS_ANY_OF) return VALUE_INPUT.MULTI;
  return VALUE_INPUT.TEXT;
};

const keepsValues = (current: ValueInputKind, next: ValueInputKind): boolean =>
  current === VALUE_INPUT.TEXT && next === VALUE_INPUT.TEXT;

export const useConditionEditor = ({
  ruleIndex,
  conditionIndex,
  fields,
}: UseConditionEditorParams) => {
  const {
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext<SegmentFormValues>();

  const path = `rules.${ruleIndex}.conditions.${conditionIndex}` as const;
  const condition: FormCondition | undefined = watch(path);
  const conditionErrors =
    errors.rules?.[ruleIndex]?.conditions?.[conditionIndex];

  const selectedField = fields.find(
    (field) => field.value === condition?.field,
  );
  const valueInput =
    selectedField && condition?.operator
      ? getValueInput(condition.operator)
      : VALUE_INPUT.NONE;

  const setOperator = (operator: OperatorValue) => {
    setValue(`${path}.operator`, operator, { shouldDirty: true });
    clearErrors(`${path}.operator`);
  };

  const clearValues = () => {
    setValue(`${path}.values`, [], { shouldDirty: true });
    clearErrors(`${path}.values`);
  };

  const handleFieldChange = (selected: string) => {
    const field = fields.find((candidate) => candidate.value === selected);
    if (!field) return;
    setValue(`${path}.field`, field.value, { shouldDirty: true });
    clearErrors(`${path}.field`);
    setOperator(field.defaultOperator);
    clearValues();
  };

  const handleOperatorChange = (selected: string) => {
    const parsed = operatorSchema.safeParse(selected);
    if (
      !parsed.success ||
      !selectedField ||
      parsed.data === condition?.operator
    )
      return;
    setOperator(parsed.data);
    if (keepsValues(valueInput, getValueInput(parsed.data))) return;
    clearValues();
  };

  const handleValuesChange = (values: string[]) => {
    setValue(`${path}.values`, values, { shouldDirty: true });
    setValue(`${path}.isValueEdited`, valueInput === VALUE_INPUT.TEXT);
    clearErrors(`${path}.values`);
  };

  const handleInversionChange = (checked: boolean) =>
    setValue(`${path}.isInverted`, checked, { shouldDirty: true });

  return {
    condition,
    conditionErrors,
    selectedField,
    valueInput,
    handleFieldChange,
    handleOperatorChange,
    handleValuesChange,
    handleInversionChange,
  };
};
